import { useMemo } from "react";
import _ from "lodash";

import { InfoOutlinedIcon } from "icons";
import { colors } from "theme/palette";

import Box from "components/Box";
import Button from "components/Button";
import Dialog, { DialogActions, DialogTitle } from "components/Dialog";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";

import { useFieldRemarks } from "entities/Field/sdk";

interface IDropdownFieldInstructionsDialog {
  open: boolean;
  handleClose: () => void;
  packageId: number;
  dealId: number;
  fieldId: number;
  showPrivateInstructions: boolean;
}
const DropdownFieldInstructionsDialog = ({
  open,
  handleClose,
  packageId,
  dealId,
  fieldId,
  showPrivateInstructions,
}: IDropdownFieldInstructionsDialog) => {
  const { data: remarkData, loading } = useFieldRemarks({
    dealId,
    packageId,
    fieldId,
    enabled: open,
  });
  const internalInstructionsFormattedValue = useMemo(() => {
    if (!_.isNil(remarkData?.internal_instructions)) {
      return EMPTY_VALUE_REGEX.test(remarkData?.internal_instructions)
        ? ""
        : remarkData?.internal_instructions;
    }
    return "";
  }, [remarkData?.internal_instructions]);

  const externalInstructionsFormattedValue = useMemo(() => {
    if (!_.isNil(remarkData?.external_instructions)) {
      return EMPTY_VALUE_REGEX.test(remarkData?.external_instructions)
        ? ""
        : remarkData?.external_instructions;
    }
    return "";
  }, [remarkData?.external_instructions]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
      {!_.isNil(remarkData) && !loading && (
        <Box sx={{ padding: 1 }}>
          <DialogTitle>{remarkData.name}</DialogTitle>
          <Stack spacing={2} sx={{ paddingX: 3 }}>
            {showPrivateInstructions && (
              <>
                <Stack alignItems="center" direction="row" spacing={1}>
                  <InfoOutlinedIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: colors.pink100,
                    }}
                  />
                  <Text variant="label" color={colors.pink100}>
                    Reviewer Instructions
                  </Text>
                </Stack>
                <Text
                  variant="text4"
                  sx={{
                    paddingLeft: 1,
                  }}
                  data-testid="instructions-value"
                >
                  {!_.isEmpty(internalInstructionsFormattedValue) ? (
                    <SanitizeHtml html={internalInstructionsFormattedValue} />
                  ) : (
                    <Text
                      variant="text2"
                      sx={{
                        color: colors.gray60,
                      }}
                    >
                      Reviewer instructions for this field are not provided.
                    </Text>
                  )}
                </Text>
              </>
            )}
            <Stack alignItems="center" direction="row" spacing={1}>
              <InfoOutlinedIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.blue100,
                }}
              />
              <Text variant="label" color={colors.blue100}>
                {showPrivateInstructions
                  ? "Public Instructions"
                  : "Instructions"}
              </Text>
            </Stack>
            <Text
              variant="text4"
              sx={{
                paddingLeft: 1,
              }}
              data-testid="instructions-value"
            >
              {!_.isEmpty(externalInstructionsFormattedValue) ? (
                <SanitizeHtml html={externalInstructionsFormattedValue} />
              ) : (
                <Text
                  variant="text2"
                  sx={{
                    color: colors.gray60,
                  }}
                >
                  {showPrivateInstructions
                    ? "Public instructions"
                    : "Instructions"}{" "}
                  for this field are not provided.
                </Text>
              )}
            </Text>
          </Stack>
          <DialogActions>
            <Button
              variant="text"
              onClick={handleClose}
              data-testid="close-dialog-button"
            >
              Close
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

export default DropdownFieldInstructionsDialog;
